import { z } from "../zod.js";

// ═══════════════════════════════════════════════════════════════════════════
//  PLATFORM-LAYER domain events — the catalog for names emitted by
//  `apps/api/src/platform/**` rather than by any one module.
// ═══════════════════════════════════════════════════════════════════════════
//
// WHY THIS FILE EXISTS. `platform.event_apply_failed` shipped as a first-class
// domain event — appended to the outbox, documented in spec 06 §6 and D-058
// pt 5, and promised to Module 12 as something it can "subscribe to with zero
// rework" — while appearing in NO contracts catalog. That put it outside the
// D-015 producer-parity gate in two independent ways: the gate scanned only
// per-module source under `src/modules/**`, and the emit site used a constant
// rather than a literal. An event with no typed contract is exactly the rework
// the promise said would not be needed, so it gets one here, and
// `module-catalog-parity.test.ts` now scans `src/platform/**` against this
// list — both directions, like every other module's.
//
// THE LANE, STATED BECAUSE IT IS THE EXCEPTION. Spec 15 §6 defines every
// `platform.*` event as riding `platform.outbox` on the `platform-events`
// topic. `platform.event_apply_failed` is the ONE `platform.*` name written to
// the TENANT outbox (`public.outbox`) instead, because it must carry the failed
// event's `tenant_id` for Module 12 to route the exception to the right
// tenant's queue — a platform-lane row has no tenant to route by. Spec 15 §6
// records the exception explicitly; a Module 12 author who reads only spec 15
// would otherwise subscribe to the wrong lane.

/**
 * Actor kind on the outbox envelope, as the code actually spells it
 * (`OutboxActor.kind` in `platform/outbox/outbox.service.ts`). The relay emits
 * this event with `kind: "system"`.
 */
export const PlatformEventActorKind = z.enum(["human", "agent", "system"]);

/**
 * `platform.event_apply_failed` — raised when an outbox event exhausts its
 * delivery budget and is parked (spec 06 §6, spec 02 §6.2, D-058 pt 5).
 *
 * Module 12 turns this into an `event_apply_failed` exception. Until Module 12
 * exists the relay also logs it at ERROR, so the signal is never silent on a
 * build with no consumer.
 *
 * `source_payload` deliberately COPIES the parked event's whole payload onto
 * this row. That duplicates whatever the original carried, which is a real
 * cost — and it is the right trade: the operator resolving a dead letter needs
 * to see what could not be applied without a second query against a table whose
 * row they may be about to discard, and the copy keeps the signal
 * self-contained when it becomes an SQS message body.
 */
export const PlatformEventApplyFailedPayload = z.object({
  /** The parked outbox row's id. */
  event_id: z.guid(),
  /** The parked row's event name (D-015 grammar). */
  event_name: z.string(),
  /** DELIVERY FAILURES spent — the retry budget (`delivery_failures`), max 5. */
  attempts: z.number().int().nonnegative(),
  /** CLAIMS the row took — `attempt_count`, the crash-loop counter. Higher than
   * `attempts` whenever a deploy, eviction or lapsed lease re-claimed the row.  */
  claims: z.number().int().nonnegative(),
  /** The last failure reason, truncated to 2000 chars on the row. */
  error: z.string(),
  /** The parked event's own `occurred_at`, ISO-8601 — NOT this signal's. */
  occurred_at: z.string(),
  /** The parked event's payload, verbatim. */
  source_payload: z.record(z.string(), z.unknown()),
});

/**
 * Every event name the PLATFORM layer emits. Gated both ways against
 * `apps/api/src/platform/**` by `module-catalog-parity.test.ts`: a name here
 * that nothing emits fails, and so does a `platform.*` literal emitted from the
 * platform layer that is absent here.
 */
export const platformProducerEventNames = ["platform.event_apply_failed"] as const;

export type PlatformProducerEventName = (typeof platformProducerEventNames)[number];

/** Payload schema by event name — the parse surface a consumer uses. */
export const platformEventPayloads = {
  "platform.event_apply_failed": PlatformEventApplyFailedPayload,
} as const satisfies Record<PlatformProducerEventName, z.ZodType>;

/**
 * The platform layer registers no outbox SUBSCRIBERS. It is the transport: a
 * platform component consuming a domain event would be a layering inversion
 * (the relay would be delivering to itself). Stated as an exported empty list
 * so the §6 consumer gate can assert it rather than assume it.
 */
export const platformConsumedEventNames = [] as const;
